import React, { useState, useEffect, useMemo, useCallback } from "react";
import { IconButton, Tooltip, MenuItem, TextField, Box } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";

import CreateNotificationModal from "../components/notifications/CreateNotificationModal";
import ViewNotificationModal from "../components/notifications/ViewNotificationModal";

import {
  getAllNotifications,
  getNotificationById,
} from "../redux/actions/notificationActions";
import { notificationActions } from "../redux/slices/notification";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { NotificationSummaryDto } from "../shared/dtos/notification/NotificationSummaryDto";
import { NotificationStatus } from "../shared/enums/NotificationStatus";

const NotificationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux State ---
  const {
    items: notifications,
    loadingFetchAll: loadingNotifications,
    pagination,
    serverStatus,
    item: notification,
    loadingFetchOne: loadingNotification,
    loadingCreate: loadingNotificationCreate,
    errorCreate: errorCreatingNotification,
    errorFetchOne: errorLoadingNotification,
  } = useAppSelector((state) => state.notification);

  // --- Local State ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal Control
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);

  // --- Permissions ---
  const canCreate = hasPermission("notification:create");

  const fetchList = useCallback(() => {
    dispatch(
      getAllNotifications({
        search: debouncedSearch,
        status: statusFilter ? (statusFilter as NotificationStatus) : undefined,
        page: pageConfig.page,
        size: pageConfig.limit,
      }),
    );
  }, [dispatch, debouncedSearch, statusFilter, pageConfig]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Handle Success & Auto-close
  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      setIsCreateOpen(false);
      fetchList();
    }
    dispatch(notificationActions.setServerStatus(null));
  }, [serverStatus, dispatch, fetchList]);

  const columns: TableColumn<NotificationSummaryDto>[] = useMemo(
    () => [
      {
        id: "targetUserName",
        label: "Recipient",
        minWidth: 150,
        format: (_, row) => <Box component="b">{row.targetUserName}</Box>,
      },
      { id: "subject", label: "Subject Line", minWidth: 250 },
      { id: "channel", label: "Channel", minWidth: 100 },
      {
        id: "status",
        label: "Delivery Status",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      {
        id: "isRead",
        label: "Read",
        minWidth: 80,
        format: (_, row) => (row.isRead ? "✅ Yes" : "🕒 No"),
      },
      {
        id: "createdAt",
        label: "Created On",
        minWidth: 160,
        format: (_, row) => new Date(row.createdAt).toLocaleString(),
      },
    ],
    [],
  );

  const renderRowActions = (row: NotificationSummaryDto): React.JSX.Element => (
    <Tooltip title="View Full Details">
      <IconButton
        color="info"
        onClick={() => {
          dispatch(getNotificationById(row.id));
          setIsViewOpen(true);
        }}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <PageContainer
      title="System Notifications"
      subtitle="Monitor communication delivery and dispatch manual alerts."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            New Notification
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((p) => ({ ...p, page: 1 }));
        }}
        searchPlaceholder="Search recipient or subject..."
      >
        <TextField
          select
          label="Filter Status"
          size="small"
          sx={{ minWidth: 150 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="SENT">Sent</MenuItem>
          <MenuItem value="FAILED">Failed</MenuItem>
          <MenuItem value="READ">Read</MenuItem>
        </TextField>
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={notifications}
        loading={loadingNotifications}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        onPageChange={(p) => setPageConfig({ ...pageConfig, page: p })}
        onRowsPerPageChange={(l) =>
          setPageConfig({ ...pageConfig, limit: l, page: 1 })
        }
        rowAction={renderRowActions}
      />

      {/* --- MODALS --- */}
      {isCreateOpen && (
        <CreateNotificationModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          loading={loadingNotificationCreate}
          error={errorCreatingNotification}
        />
      )}

      {isViewOpen && (
        <ViewNotificationModal
          open={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          notification={notification}
          loading={loadingNotification}
          error={errorLoadingNotification}
        />
      )}
    </PageContainer>
  );
};

export default NotificationsPage;
